import { Skeleton } from 'primereact/skeleton';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useSetAtom } from 'jotai';
import { Card } from 'primereact/card';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { selectWidgetAIAtom } from '~/atoms/dashboardAtom';
import { openPopoutWindow } from '~/utils/popoutUtils';
import { NoDataAvailableTemplate } from '../../common/NoDataAvailableTemplate';
import { WidgetHeader } from '../../common/WidgetHeader';
import { MetricLabel } from '../../common/MetricLabel';
import { exportWidgetToPDF } from '../common/pdfExportUtils';

export interface HistoricalReturnsData {
  snapshot_id: string;
  portfolio_id: string;
  period: {
    start_date: string;
    end_date: string;
    days: number;
  };
  performance_metrics: {
    current_portfolio: PerformanceMetrics | null;
    proposed_portfolio: PerformanceMetrics;
  };
  daily_cumulative_returns: {
    dates: string[];
    current_portfolio: number[] | null;
    proposed_portfolio: number[];
    benchmark?: number[] | null;
    summary: {
      current_total_return: number;
      proposed_total_return: number;
    };
  };
  fx_metrics: {
    fx_exposure: number;
    fx_var_95: number;
    fx_volatility: number;
    currency_concentration: Record<string, number>;
  };
  allocation_summary: {
    total_allocation: number;
    cash_allocation: number;
    sector_breakdown: Record<string, number>;
  };
  benchmark_data: any | null;
}

export interface PerformanceMetrics {
  total_return: number;
  annualized_return: number;
  sharpe_ratio: number;
  volatility: number;
  beta: number;
  max_drawdown: number;
  var_30d_95: number;
}

export type HistoricalReturnsWidgetProps = {
  data: HistoricalReturnsData | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  handleRemoveWidget?: (widgetId: string) => void;
  widgetId: string;
  title?: string;
  snapshotId?: string | null;
  benchmarkName?: string | null;
  portfolioId?: string | null;
  portfolioName?: string | null;
};

export const HistoricalReturnsWidget: React.FC<
  HistoricalReturnsWidgetProps
> = ({
  data,
  isLoading,
  error,
  refetch,
  handleRemoveWidget,
  widgetId,
  title = 'Historical Returns',
  snapshotId,
  benchmarkName,
  portfolioId,
  portfolioName,
}) => {
  const setSelectWidgetAI = useSetAtom(selectWidgetAIAtom);
  const widgetRef = useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = useState(false);

  const removeWidget = useCallback(
    () => handleRemoveWidget?.(widgetId),
    [widgetId, handleRemoveWidget],
  );

  // Add popout functionality
  const handlePopout = useCallback(() => {
    openPopoutWindow({
      widgetType: 'historical-returns',
      snapshotId: snapshotId || undefined,
      title: title || 'Historical Returns',
      benchmarkName: benchmarkName || undefined,
      portfolioId: portfolioId || undefined,
      portfolioName: portfolioName || undefined,
    });
  }, [snapshotId, title, benchmarkName, portfolioId, portfolioName]);

  // Export to PDF
  const handleExport = useCallback(async () => {
    if (!widgetRef.current || !data) {
      console.warn('Cannot export: widget element or data not available');
      return;
    }

    // Build summary metrics for header
    let headerRightText = '';
    if (data?.performance_metrics) {
      const summaryParts: string[] = [];
      if (
        data.performance_metrics.current_portfolio?.total_return !== undefined
      ) {
        summaryParts.push(
          `Current: ${data.performance_metrics.current_portfolio.total_return.toFixed(2)}%`,
        );
      }
      if (data.benchmark_data?.cumulative_return !== undefined) {
        summaryParts.push(
          `${benchmarkName || 'Benchmark'}: ${data.benchmark_data.cumulative_return.toFixed(2)}%`,
        );
      }
      if (
        data.performance_metrics.proposed_portfolio?.total_return !== undefined
      ) {
        summaryParts.push(
          `AI: ${data.performance_metrics.proposed_portfolio.total_return.toFixed(2)}%`,
        );
      }
      if (summaryParts.length > 0) {
        headerRightText = summaryParts.join(' | ');
      }
    }

    setIsExporting(true);
    try {
      await exportWidgetToPDF({
        widgetElement: widgetRef.current,
        title,
        portfolioName,
        headerRightText,
      });
    } catch (error) {
      console.error('Failed to export PDF:', error);
    } finally {
      setIsExporting(false);
    }
  }, [widgetRef, data, title, portfolioName, benchmarkName]);

  // Transform data for chart
  const chartData = useMemo(() => {
    if (!data?.daily_cumulative_returns) return [];

    const { dates, current_portfolio, proposed_portfolio, benchmark } =
      data.daily_cumulative_returns;

    return dates.map((date, index) => ({
      date: new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      }),
      current: current_portfolio?.[index] || 0,
      aiRecommended: proposed_portfolio?.[index] || 0,
      benchmark: benchmark?.[index] || 0,
    }));
  }, [data]);

  // Check if benchmark data is available
  const hasBenchmarkData = useMemo(() => {
    return (
      data?.daily_cumulative_returns?.benchmark &&
      data.daily_cumulative_returns.benchmark.length > 0
    );
  }, [data]);

  const noData = useMemo(() => {
    return !data || !data.daily_cumulative_returns;
  }, [data]);

  // Calculate Y-axis ticks and domain using fixed percentage intervals
  const yAxisConfig = useMemo(() => {
    if (!chartData.length) return { ticks: undefined, domain: undefined };

    // Find min and max values across all data
    const allValues = chartData.flatMap((d) =>
      [d.current, d.aiRecommended, d.benchmark].filter(
        (v): v is number => v !== null && v !== 0,
      ),
    );

    if (allValues.length === 0) return { ticks: undefined, domain: undefined };

    const minValue = Math.min(...allValues);
    const maxValue = Math.max(...allValues);

    // Define available tick values: 0%, ±5%, ±10%, ±15%, ±20%, ±30%
    const availableTicks = [-30, -20, -15, -10, -5, 0, 5, 10, 15, 20, 30];

    // Select ticks that cover the data range
    const selectedTicks = availableTicks.filter(
      (tick) =>
        tick >= Math.floor(minValue) - 5 && tick <= Math.ceil(maxValue) + 5,
    );

    // Ensure we have at least 3 ticks and always include 0
    if (!selectedTicks.includes(0)) {
      selectedTicks.push(0);
      selectedTicks.sort((a, b) => a - b);
    }

    // Set domain to first and last tick
    const domain = [
      selectedTicks[0],
      selectedTicks[selectedTicks.length - 1],
    ] as [number, number];

    return { ticks: selectedTicks, domain };
  }, [chartData]);

  if (isLoading) {
    return (
      <div className="p-3 h-full">
        <Skeleton className="h-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full hover-container" id={widgetId} ref={widgetRef}>
        <WidgetHeader
          title={title}
          removeWidget={removeWidget}
          refetch={() => refetch()}
          setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
          onPopout={handlePopout}
          onExport={data ? handleExport : undefined}
          isExporting={isExporting}
          widgetId={widgetId}
        />
        <NoDataAvailableTemplate
          icon="pi pi-exclamation-triangle"
          title="Something went wrong."
          isError
        />
      </div>
    );
  }

  if (!snapshotId) {
    return (
      <div className="h-full hover-container" id={widgetId} ref={widgetRef}>
        <WidgetHeader
          title={title}
          removeWidget={removeWidget}
          setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
          onPopout={handlePopout}
          widgetId={widgetId}
        />
        <NoDataAvailableTemplate
          icon="pi pi-exclamation-triangle"
          title="No Snapshot Available."
          subTitle="Run portfolio analysis to generate snapshot"
        />
      </div>
    );
  }

  if (noData) {
    return (
      <div className="h-full hover-container" id={widgetId} ref={widgetRef}>
        <WidgetHeader
          title={title}
          removeWidget={removeWidget}
          refetch={() => refetch()}
          setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
          onPopout={handlePopout}
          widgetId={widgetId}
        />
        <NoDataAvailableTemplate
          title="No historical returns data available."
          subTitle="Run portfolio analysis to generate historical returns"
          icon="pi pi-chart-line"
        />
      </div>
    );
  }

  return (
    <div
      className="hover-container h-full flex flex-column"
      id={widgetId}
      ref={widgetRef}
    >
      <WidgetHeader
        title={title}
        removeWidget={removeWidget}
        refetch={() => refetch()}
        setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
        onPopout={handlePopout}
        onExport={handleExport}
        isExporting={isExporting}
        widgetId={widgetId}
      />

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-3 h-full">
          <Card
            className="h-full"
            pt={{
              content: {
                className: 'p-0 m-0 h-full flex flex-column',
              },
              body: {
                className: 'h-full',
              },
            }}
          >
            {/* Performance Summary */}
            <div
              className="pb-2 border-bottom-1 mb-2 flex-shrink-0"
              style={{ borderColor: 'var(--bq-border-muted)' }}
            >
              <div className="grid">
                <div className={hasBenchmarkData ? 'col-4' : 'col-6'}>
                  <div className="text-center">
                    <div className="text-xl font-bold text-blue-400">
                      {data?.performance_metrics.current_portfolio?.total_return?.toFixed(
                        2,
                      ) || '0.00'}
                      %
                    </div>
                    <div
                      className="text-xs"
                      style={{ color: 'var(--bq-text-tertiary)' }}
                    >
                      Current Allocation
                    </div>
                  </div>
                </div>
                {hasBenchmarkData && (
                  <div className="col-4">
                    <div className="text-center">
                      <div className="text-xl font-bold text-orange-400">
                        {data?.benchmark_data?.cumulative_return?.toFixed(2) ||
                          '0.00'}
                        %
                      </div>
                      <div
                        className="text-xs"
                        style={{ color: 'var(--bq-text-tertiary)' }}
                      >
                        {benchmarkName || 'Benchmark'}
                      </div>
                    </div>
                  </div>
                )}
                <div className={hasBenchmarkData ? 'col-4' : 'col-6'}>
                  <div className="text-center">
                    <div
                      className="text-xl font-bold"
                      style={{ color: 'var(--bq-chart-ai)' }}
                    >
                      {data?.performance_metrics.proposed_portfolio?.total_return?.toFixed(
                        2,
                      ) || '0.00'}
                      %
                    </div>
                    <div
                      className="text-xs"
                      style={{ color: 'var(--bq-text-tertiary)' }}
                    >
                      AI Portfolio
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Chart */}
            <div className="flex-1 min-h-0" style={{ minHeight: '150px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="var(--bq-chart-grid)"
                  />
                  <XAxis
                    dataKey="date"
                    stroke="var(--bq-color-neutral)"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    stroke="var(--bq-color-neutral)"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value) => `${value.toFixed(1)}%`}
                    ticks={yAxisConfig.ticks}
                    domain={yAxisConfig.domain}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'var(--bq-tooltip-bg)',
                      border: '1px solid var(--bq-tooltip-border)',
                      borderRadius: '6px',
                      color: 'var(--bq-tooltip-text)',
                    }}
                    formatter={(value: number, name: string) => [
                      `${value.toFixed(2)}%`,
                      name === 'current'
                        ? 'Current Allocation'
                        : name === 'benchmark'
                          ? benchmarkName || 'Benchmark'
                          : 'AI Portfolio',
                    ]}
                    labelFormatter={(label) => `Date: ${label}`}
                  />
                  <Legend
                    wrapperStyle={{ color: 'var(--bq-tooltip-text)' }}
                    formatter={(value) =>
                      value === 'current'
                        ? 'Current Allocation'
                        : value === 'benchmark'
                          ? benchmarkName || 'Benchmark'
                          : 'AI Portfolio'
                    }
                  />
                  <Line
                    type="monotone"
                    dataKey="current"
                    stroke="var(--bq-chart-current)"
                    strokeWidth={2}
                    dot={false}
                    name="current"
                  />
                  {hasBenchmarkData && (
                    <Line
                      type="monotone"
                      dataKey="benchmark"
                      stroke="var(--bq-chart-benchmark)"
                      strokeWidth={2}
                      dot={false}
                      name="benchmark"
                    />
                  )}
                  <Line
                    type="monotone"
                    dataKey="aiRecommended"
                    stroke="var(--bq-chart-ai)"
                    strokeWidth={2}
                    dot={false}
                    name="aiRecommended"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Additional Metrics */}
            <div
              className="mt-2 pt-2 border-top-1 flex-shrink-0"
              style={{ borderColor: 'var(--bq-border-muted)' }}
            >
              <div className="grid">
                <div className="col-4">
                  <div className="text-center">
                    <div className="text-base font-semibold text-blue-400">
                      {data?.performance_metrics.current_portfolio?.sharpe_ratio?.toFixed(
                        2,
                      ) || '0.00'}
                    </div>
                    <div
                      className="text-xs"
                      style={{ color: 'var(--bq-text-tertiary)' }}
                    >
                      Sharpe Ratio
                    </div>
                  </div>
                </div>
                <div className="col-4">
                  <div className="flex align-items-center justify-content-center h-full">
                    <MetricLabel
                      label="Methodology"
                      description={
                        <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                          <div className="mb-3">
                            <h4
                              className="text-base font-semibold mb-2"
                              style={{ color: 'var(--bq-text-primary)' }}
                            >
                              How Historical Returns Are Calculated
                            </h4>
                            <p className="mb-2">
                              Historical returns show the actual performance of
                              each portfolio allocation over the historical
                              period, using real market data. These are
                              backward-looking calculations based on observed
                              returns.
                            </p>
                          </div>

                          <div className="mb-3">
                            <h4
                              className="text-base font-semibold mb-2"
                              style={{ color: 'var(--bq-text-primary)' }}
                            >
                              Calculation Methodology
                            </h4>

                            <div className="mb-2">
                              <p className="font-semibold mb-1">
                                1. Portfolio Returns
                              </p>
                              <p className="mb-1">
                                For each portfolio allocation (Current, AI
                                Portfolio, Benchmark), daily returns are
                                calculated as:
                              </p>
                              <ul className="list-disc ml-4 mb-0">
                                <li>
                                  Portfolio Return = Σ(weight<sub>i</sub> ×
                                  asset_return<sub>i</sub>) for all assets
                                </li>
                                <li>
                                  Cumulative returns compound these daily
                                  returns over time
                                </li>
                                <li>
                                  Returns are calculated using the actual
                                  historical prices and allocation weights
                                </li>
                              </ul>
                            </div>

                            <div className="mb-2">
                              <p className="font-semibold mb-1">
                                2. Performance Metrics
                              </p>
                              <p className="mb-1">
                                Risk-adjusted metrics are calculated from the
                                historical return series:
                              </p>
                              <ul className="list-disc ml-4 mb-0">
                                <li>
                                  <strong>Sharpe Ratio:</strong> (Return -
                                  Risk-free rate) / Volatility, measuring
                                  risk-adjusted returns
                                </li>
                                <li>
                                  <strong>Volatility:</strong> Standard
                                  deviation of daily returns, annualized
                                </li>
                                <li>
                                  <strong>Max Drawdown:</strong> Largest
                                  peak-to-trough decline
                                </li>
                                <li>
                                  <strong>Beta:</strong> Sensitivity to market
                                  movements (vs benchmark)
                                </li>
                              </ul>
                            </div>

                            <div className="mb-2">
                              <p className="font-semibold mb-1">
                                3. EWMA Expected Returns
                              </p>
                              <p>
                                For forward-looking analysis, Exponentially
                                Weighted Moving Average (EWMA) is used to
                                calculate expected returns, giving more weight
                                to recent observations while considering
                                longer-term trends.
                              </p>
                            </div>
                          </div>

                          <div className="mb-3">
                            <h4
                              className="text-base font-semibold mb-2"
                              style={{ color: 'var(--bq-text-primary)' }}
                            >
                              Portfolio Comparisons
                            </h4>
                            <p className="mb-1">
                              The chart displays three return trajectories:
                            </p>
                            <ul className="list-disc ml-4 mb-2">
                              <li>
                                <strong>Current Allocation:</strong> Performance
                                based on the current portfolio weights
                              </li>
                              <li>
                                <strong>AI Portfolio:</strong> Performance based
                                on the optimized allocation weights
                              </li>
                              <li>
                                <strong>Benchmark:</strong> Performance of the
                                selected market index or reference portfolio
                              </li>
                            </ul>
                            <p>
                              All calculations use the same historical price
                              data, ensuring fair comparison. The only
                              difference is the allocation weights applied to
                              each asset.
                            </p>
                          </div>

                          <div>
                            <h4
                              className="text-base font-semibold mb-2"
                              style={{ color: 'var(--bq-text-primary)' }}
                            >
                              Key Points
                            </h4>
                            <ul className="list-disc ml-4 mb-0">
                              <li>
                                Returns are based on actual historical market
                                data
                              </li>
                              <li>
                                Portfolio weights are held constant over the
                                period (no rebalancing)
                              </li>
                              <li>
                                Transaction costs are not included in the
                                historical returns
                              </li>
                              <li>
                                Past performance does not guarantee future
                                results
                              </li>
                            </ul>
                          </div>
                        </div>
                      }
                      width="44rem"
                    />
                  </div>
                </div>
                <div className="col-4">
                  <div className="text-center">
                    <div
                      className="text-base font-semibold"
                      style={{ color: 'var(--bq-chart-ai)' }}
                    >
                      {data?.performance_metrics.proposed_portfolio?.sharpe_ratio?.toFixed(
                        2,
                      ) || '0.00'}
                    </div>
                    <div
                      className="text-xs"
                      style={{ color: 'var(--bq-text-tertiary)' }}
                    >
                      Sharpe Ratio
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};
